import { computed, ref } from 'vue'
import axios from '@/utils/axios'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import useAcademySelection from '@/composables/useAcademySelection'

export default function useQuestionnaires() {
  const RESOURCE = '/questionnaire'
  const MAINTENANCE_RESOURCE = '/maintenance/questionnaire'
  const { showSnackbar } = useSnackbar()
  const { getContentScopeParams } = useAcademySelection()

  const baseHeaders = [
    { text: 'NAME', value: 'name' },
    { text: 'TYPE', value: 'type', width: 140 },
    { text: 'CATEGORY', value: 'category', width: 240 },
    { text: 'ACADEMY NAME', value: 'academyName', width: 220 },
    {
      text: 'ACTIONS', value: 'actions', sortable: false, width: 130, align: 'center',
    },
  ]

  const dtHeaders = computed(() => [...baseHeaders])
  const dtSearchQuery = ref('')
  const questionnaires = ref([])
  const isLoading = ref(false)

  const normalizeResponse = data => {
    if (Array.isArray(data)) return data
    if (Array.isArray(data?.questionnaires)) return data.questionnaires
    if (Array.isArray(data?.items)) return data.items
    if (Array.isArray(data?.data)) return data.data

    return []
  }

  const fetchQuestionnaires = async () => {
    isLoading.value = true

    try {
      const response = await axios.get(RESOURCE, { params: getContentScopeParams() })
      questionnaires.value = normalizeResponse(response.data).map(item => ({
        ...item,
        id: item.id || item._id,
      }))
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null

      showSnackbar(
        `Failed to fetch questionnaires:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )
    } finally {
      isLoading.value = false
    }
  }

  const deleteQuestionnaire = async questionnaire => {
    isLoading.value = true

    try {
      await axios.delete(`${MAINTENANCE_RESOURCE}/${questionnaire.id}`)
      showSnackbar('Questionnaire deleted successfully', SnackbarType.SUCCESS)
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null

      showSnackbar(
        `Failed to delete questionnaire:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )
      throw error
    } finally {
      isLoading.value = false
    }
  }

  return {
    dtHeaders,
    dtSearchQuery,
    questionnaires,
    isLoading,
    fetchQuestionnaires,
    deleteQuestionnaire,
  }
}
